import { SECURE_BASE_URL } from '../../config';
import { SIGNUP_AUTH_SUCCESS } from '../../constants/analytics';
import { UserInfoAPIResponse } from '../../types/auth';
import { SignupStep } from '../../types/billing';
import { AppDispatch, DispatchThunk } from '../../types/common';
import { APIError } from '../../types/response';
import { DEFAULT_ERROR, FAIL_TYPE_AUTH_TOKEN_RESENT } from '../../utils/auth-errors';
import { authRequest } from '../../utils/auth-request';
import { AuthState } from '../defaults/auth';
import { getIsRegistering } from '../selectors/authProgress';
import { getIsAuthenticated } from '../selectors/billing';
import { track, tryToIdentify } from './analytics';
import { setAuthConfig } from './authConfig';
import { setSignupStep } from './billing';
import { setAuthMessage } from './common';

// MM: exported for storybook testing
export const REGISTRATION_STATUS_URL = `${SECURE_BASE_URL}/auth/info/register`;
export const REGISTERED_CHECK_URL = `${SECURE_BASE_URL}/login/check`;

export const SET_SENDING_AUTH_REQUEST = 'APP_AUTH_SET_SENDING_AUTH_REQUEST';
export const SEND_AUTH_REQUEST = 'APP_AUTH_SEND_AUTH_REQUEST';
export const RECEIVE_AUTH_REQUEST = 'APP_AUTH_RECEIVE_AUTH_REQUEST';
export const FAIL_AUTH_REQUEST = 'APP_AUTH_FAIL_AUTH_REQUEST';
export const CLEAR_AUTH_REQUEST_FAILURE = 'APP_AUTH_CLEAR_AUTH_REQUEST_FAILURE';
export const SET_IS_AUTH_RESTART_REQUIRED = 'APP_SET_IS_AUTH_RESTART_REQUIRED';
export const SET_USER_LOCATION = 'SET_USER_LOCATION';
export const SET_IS_USER_IN_PHILO_SUPPORTED_GEO_REGION =
  'APP_SET_IS_USER_IN_PHILO_SUPPORTED_GEO_REGION';
export const SET_INPUT_STATUS = 'APP_SET_INPUT_STATUS';
export const SET_INPUT_FOCUSED = 'APP_SET_INPUT_FOCUSED';
export const SET_AUTH_CODE = 'APP_SET_AUTH_CODE';
export const SET_AUTH_CODE_STATUS = 'APP_SET_AUTH_STATUS';
export const SET_SHOULD_REQUEST_NEW_AUTH_CODE = 'APP_SET_SHOULD_REQUEST_NEW_AUTH_CODE';

export function setInputStatus(inputStatus?: AuthState['inputStatus']) {
  return {
    type: SET_INPUT_STATUS,
    inputStatus,
  };
}

export function setInputFocused(inputFocused: AuthState['inputFocused']) {
  return {
    type: SET_INPUT_FOCUSED,
    inputFocused,
  };
}

export function sendAuthRequest() {
  return {
    type: SEND_AUTH_REQUEST,
  };
}

export function receiveAuthRequest() {
  return {
    type: RECEIVE_AUTH_REQUEST,
  };
}

export function failAuthRequest({ message, errorCode = null }: Partial<AuthState>) {
  return {
    type: FAIL_AUTH_REQUEST,
    message,
    errorCode,
  };
}

export function clearAuthRequestFailure() {
  return {
    type: CLEAR_AUTH_REQUEST_FAILURE,
  };
}

export function setIsAuthRestartRequired(
  isAuthRestartRequired: AuthState['isAuthRestartRequired'],
) {
  return {
    type: SET_IS_AUTH_RESTART_REQUIRED,
    isAuthRestartRequired,
  };
}

export function setUserLocation(location: AuthState['location']) {
  return {
    type: SET_USER_LOCATION,
    location,
  };
}

export function setIsUserInPhiloSupportedGeoRegion(
  isUserInPhiloSupportedGeoRegion: AuthState['isUserInPhiloSupportedGeoRegion'],
) {
  return {
    type: SET_IS_USER_IN_PHILO_SUPPORTED_GEO_REGION,
    isUserInPhiloSupportedGeoRegion,
  };
}

export function setAuthCode(authCode: AuthState['authCode']) {
  return {
    type: SET_AUTH_CODE,
    authCode,
  };
}

export function setAuthCodeStatus(authCodeStatus?: AuthState['authCodeStatus']) {
  return {
    type: SET_AUTH_CODE_STATUS,
    authCodeStatus,
  };
}

export function setShouldRequestAuthNewCode(
  shouldRequestAuthNewCode: AuthState['shouldRequestAuthNewCode'],
) {
  return {
    type: SET_SHOULD_REQUEST_NEW_AUTH_CODE,
    shouldRequestAuthNewCode,
  };
}

export function clearAuthCode(): DispatchThunk {
  return dispatch => {
    dispatch(setAuthCode(''));
    dispatch(setAuthCodeStatus());
  };
}

export function displaySentNewAuthCodeMessage(): DispatchThunk {
  return (dispatch, getState) => {
    const isRegistering = getIsRegistering(getState());
    dispatch(setShouldRequestAuthNewCode(false));
    dispatch(setIsAuthRestartRequired(false));
    dispatch(
      failAuthRequest({
        message: `A ${isRegistering ? 'verification' : 'sign-in'} code has been resent.`,
        // KU – we want this "error" message to be green
        errorCode: FAIL_TYPE_AUTH_TOKEN_RESENT,
      }),
    );
  };
}

export const trackSignUp = (): DispatchThunk => (dispatch, getState) => {
  const isAuthenticated = getIsAuthenticated(getState());
  if (!isAuthenticated) {
    dispatch(track(SIGNUP_AUTH_SUCCESS));
  }
};

// JD: Below are helper curry functions to share code
// across our asynchronous action creators.
// Pass in dispatch to create the desired callback.
export const createOnErrorCallback = (dispatch: AppDispatch) => (error: APIError) => {
  const { name, description } = error;
  const message = `${name}: ${description}`;
  dispatch(failAuthRequest({ message }));
};

export function createErrorMessageForGeneralCase(
  description: string,
  errorCode: AuthState['errorCode'],
) {
  let errorMessage = null;
  if (description && description.length) {
    errorMessage = description;
  } else {
    errorMessage = `Error code: ${errorCode} ${DEFAULT_ERROR}`;
  }

  return errorMessage;
}

export function getRegistrationStatus(): DispatchThunk {
  return dispatch => {
    const onAuthSuccess = ({ analytics }: UserInfoAPIResponse) => {
      dispatch(tryToIdentify(analytics));
    };

    const onAuthFailure = ({ poll, action, init }: UserInfoAPIResponse) => {
      dispatch(
        setAuthConfig({
          registerPollURL: poll,
          registerActionURL: action,
          registerInitURL: init,
        }),
      );
    };

    authRequest({
      url: REGISTRATION_STATUS_URL,
      onAuthSuccess,
      onAuthFailure,
      onError: createOnErrorCallback(dispatch),
    });
  };
}

export function clearIdentStatus(): DispatchThunk {
  return dispatch => {
    dispatch(setInputStatus());
    dispatch(setAuthMessage(''));
  };
}

export function restartAuth(): DispatchThunk {
  return dispatch => {
    dispatch(clearIdentStatus());
    dispatch(setIsAuthRestartRequired(false));
    dispatch(setSignupStep(SignupStep.CREATE));
  };
}
